/**
 * Review source deep links — the ONE registration table from a review source's
 * kind to every `?state=` seam its deep link rides (design D26 of
 * openspec/changes/artemis-scrutiny-and-curiosity).
 *
 * A review is addressable from a URL through a SET of seams, and a kind
 * registered in some of them but not all fails silently: `validateMeTabState`
 * returns a fixed key set, so a key it does not know dies on the first
 * round-trip; a key the host's write watcher does not project never reaches the
 * URL; a key the host does not hydrate opens nothing on a cold load. So a kind
 * declares its seams ONCE, here, and every seam derives from the table:
 *
 *   - `getDefaultMeTabState` / `validateMeTabState` (unifiedRouter.js) read
 *     `reviewDeepLinkDefaults` / `validateReviewDeepLinks`;
 *   - `migrateLegacyMeTabInboxShape` (unifiedRouter.js) reads
 *     `reviewDeepLinkLandingTab`;
 *   - the Me panel host's write watcher and its hydration (MeTab.vue) read
 *     `reviewDeepLinkPatch` / `reviewSourceFromDeepLink`.
 *
 * A new kind is one entry here, beside its `SOURCE_VERBS` entry in the review
 * store and its `Inbox::DeepLink` builder on the server. Writers that set these
 * keys directly (the queue's landing, the Pulse drawer, the inbox pane, the
 * Weigh-in navigation) write the same key names and are not seams of the table.
 *
 * A LEAF WITH ZERO IMPORTS, on `filterCriteriaDefaults.js`'s reason: the router
 * is imported almost everywhere and the review store imports the surface's own
 * modules, so neither can import the other without risking a cycle. Both read
 * this instead, and the review store re-exports the kind constants from here.
 */

/** The `source.kind` a thread-anchored preview review opens on. */
export const REVIEW_SOURCE_PREVIEW = "preview";

/** The `source.kind` a validation-queue group review opens on. */
export const REVIEW_SOURCE_VALIDATION_GROUP = "validation_group";

/** The `source.kind` a filing's side quests open on (asc D25, cfw D13). */
export const REVIEW_SOURCE_SIDE_QUESTS = "side_quests";

/** The `source.kind` a revision contest opens on (revision-contests RC-D11). */
export const REVIEW_SOURCE_CONTEST = "contest";

// The id contracts the registered kinds use. Each is the validator's whole rule
// for its key: a value it rejects rests at null and is never coerced, so the
// string "412" a hand-edited URL carries cannot sail past as a number, and a
// numeric group key cannot match no group with no error.
const isNumberId = (value) => typeof value === "number";
const isNonEmptyString = (value) => typeof value === "string" && value.length > 0;

/**
 * THE TABLE, keyed by `source.kind`. Each entry names:
 *
 *   - `openKey` — the strict-boolean flag: the review is open on this kind. The
 *     string "true" from a hand-edited URL rests at false rather than opening a
 *     pane on something with nothing left to decide.
 *   - `idKey` — WHICH source of the kind the review shows; null names none.
 *   - `isId(value)` — the id's contract (above).
 *   - `openPatch(source)` — the keys an open projects beside `openKey: true`.
 *   - `closePatch` — the keys a close projects beside `openKey: false`.
 *   - `toSource(meTab)` — the source the host opens from a URL carrying the flag
 *     and a valid id, or null for a kind hydrated somewhere else.
 *   - `landingTab` — the Me sub-tab a link naming this review but no sub-tab
 *     opens on (`meTabs.js`'s `tabQuery`), or null when every link minted for
 *     the kind names its sub-tab.
 *   - `excludes` (optional) — the kind this kind's key wins over: beside a
 *     valid id of this kind, the excluded kind's id rests at null, so a link
 *     naming both opens this one. Two kinds that share a flag need it, since
 *     the flag alone cannot say which of them it opens.
 *
 * Every entry is frozen. The table itself stays extensible, because an
 * assignment is exactly how a kind registers: the registry suite registers a
 * kind that way to prove no seam names a kind by hand.
 */
export const REVIEW_SOURCE_DEEP_LINKS = {
  [REVIEW_SOURCE_PREVIEW]: Object.freeze({
    openKey: "artemisReviewOpen",
    idKey: "artemisReviewPreviewId",
    isId: isNumberId,
    // The caller's EXPLICIT naming (`source.previewId`, which `openPreviewId`
    // mirrors), never `targetPreviewId`, which falls back to the sequencer's
    // pick. Writing the fallback would pin the URL to whichever changeset
    // happened to be pending, so a later refresh would reopen that one instead
    // of re-picking — the URL making a claim the Actor never made.
    openPatch: (source) => ({
      artemisReviewPreviewId: isNumberId(source.previewId) ? source.previewId : null,
    }),
    closePatch: Object.freeze({ artemisReviewPreviewId: null }),
    // THREAD-anchored: `ArtemisHub.vue` opens it AFTER `selectThread` delivers
    // the thread's pending surface, since opening first would show the
    // previous thread's changeset for the length of the fetch.
    toSource: null,
    // Minted when Artemis was the Me default, so these links name no sub-tab.
    landingTab: "Artemis",
  }),
  [REVIEW_SOURCE_VALIDATION_GROUP]: Object.freeze({
    openKey: "validationReviewOpen",
    idKey: "validationGroupId",
    isId: isNonEmptyString,
    // THE FOCUS IS PART OF THE ADDRESS, so it is projected WITH the key and
    // never left behind (WMI D10 / F38): a group opened on a row reopens on
    // that row after a refresh, and a group opened on the bundle clears the
    // row an earlier Weigh in left in the URL.
    openPatch: (source) => ({
      validationGroupId: source.key,
      validationFocusProposalId: source.focusProposalId ?? null,
    }),
    // The key STAYS on close: it is also the queue's "highlight this row"
    // hand-back, which the surface's own return writes alongside
    // `validationReviewOpen: false`. The focus does not: the queue highlights a
    // GROUP, and a row-level claim on a closed review outlives its reading.
    closePatch: Object.freeze({ validationFocusProposalId: null }),
    // NO ENVELOPE, deliberately: a reload holds no group in memory, and the
    // store refreshes a group source it was handed without one from
    // `GET /api/v2/validation_queue/groups/:key`. The validator has already
    // made the focus a number or null.
    toSource: (meTab) => ({
      kind: REVIEW_SOURCE_VALIDATION_GROUP,
      key: meTab.validationGroupId,
      focusProposalId: meTab.validationFocusProposalId ?? null,
    }),
    // Every `Inbox::DeepLink.validation_group` link names Me ▸ Validation.
    landingTab: null,
  }),
  [REVIEW_SOURCE_SIDE_QUESTS]: Object.freeze({
    openKey: "artemisSideQuestsOpen",
    // The curiosity filing whose offered facts the source lists (cfw D13: a
    // plan-less write's side quests have a home too), a number under the
    // server's one id rule (`Inbox::DeepLink.side_quests` Integer-coerces it).
    idKey: "artemisSideQuestFilingId",
    isId: isNumberId,
    openPatch: (source) => ({
      artemisSideQuestFilingId: isNumberId(source.filingId) ? source.filingId : null,
    }),
    // Nothing reads the filing after a close, so a refresh reopens nothing.
    closePatch: Object.freeze({ artemisSideQuestFilingId: null }),
    // NO THREAD, so no ordering to wait on: the host opens it as it opens a
    // group, and the source names only the filing.
    toSource: (meTab) => ({
      kind: REVIEW_SOURCE_SIDE_QUESTS,
      filingId: meTab.artemisSideQuestFilingId,
    }),
    // Every `Inbox::DeepLink.side_quests` link names Me ▸ Artemis.
    landingTab: null,
  }),
  [REVIEW_SOURCE_CONTEST]: Object.freeze({
    // The group's flag: a contest is reviewed on the same Me ▸ Validation pane,
    // and `Inbox::DeepLink.validation_contest` emits this pair verbatim.
    openKey: "validationReviewOpen",
    // The contest's Integer id, under the server's one id rule (`normalize_id`).
    idKey: "validationContestId",
    isId: isNumberId,
    openPatch: (source) => ({
      validationContestId: isNumberId(source.contestId) ? source.contestId : null,
    }),
    // Cleared on close, unlike the group's key: the flag is shared, so a
    // contest id left behind would win over the next group opened on it.
    closePatch: Object.freeze({ validationContestId: null }),
    // No thread and no envelope in memory after a reload: the store's contest
    // read fills the envelope, as the group read fills a group's.
    toSource: (meTab) => ({
      kind: REVIEW_SOURCE_CONTEST,
      contestId: meTab.validationContestId,
    }),
    // Every `Inbox::DeepLink.validation_contest` link names Me ▸ Validation,
    // as every group link does.
    landingTab: null,
    // The contest key wins (revision-contests RC-D11): a link naming a contest
    // and a group opens the contest.
    excludes: REVIEW_SOURCE_VALIDATION_GROUP,
  }),
};

const registeredLinks = () => Object.values(REVIEW_SOURCE_DEEP_LINKS);

const linkFor = (kind) =>
  kind != null && Object.hasOwn(REVIEW_SOURCE_DEEP_LINKS, kind) ? REVIEW_SOURCE_DEEP_LINKS[kind] : null;

/**
 * The kinds a `meTab` section's valid ids exclude (each entry's `excludes`),
 * read under each kind's own id contract.
 *
 * @param {Object} meTab
 * @returns {Set<string>}
 */
function excludedKinds(meTab) {
  const excluded = new Set();
  for (const link of registeredLinks()) {
    if (link.excludes != null && link.isId(meTab[link.idKey])) excluded.add(link.excludes);
  }
  return excluded;
}

/**
 * The keys that rest an excluded kind's id at null: the id alone, as the
 * validator drops it.
 *
 * @param {Object} link the entry whose `excludes` names the kind
 * @returns {Object}
 */
function exclusionPatch(link) {
  const excluded = linkFor(link.excludes);
  return excluded ? { [excluded.idKey]: null } : {};
}

/**
 * Every registered kind's resting keys: the review closed, naming nothing.
 * `getDefaultMeTabState` spreads it.
 *
 * @returns {Object}
 */
export function reviewDeepLinkDefaults() {
  const defaults = {};
  for (const link of registeredLinks()) {
    defaults[link.openKey] = false;
    defaults[link.idKey] = null;
  }
  return defaults;
}

/**
 * Every registered kind's keys, validated: a strict boolean flag and an id
 * under the kind's own contract, anything else at its default, and an id a
 * valid id of another kind excludes at null. `validateMeTabState` spreads it.
 *
 * @param {Object} meTab a raw `meTab` section
 * @returns {Object}
 */
export function validateReviewDeepLinks(meTab) {
  const validated = {};
  for (const link of registeredLinks()) {
    const open = meTab[link.openKey];
    const id = meTab[link.idKey];
    validated[link.openKey] = typeof open === "boolean" ? open : false;
    validated[link.idKey] = link.isId(id) ? id : null;
  }
  for (const kind of excludedKinds(validated)) {
    validated[linkFor(kind).idKey] = null;
  }
  return validated;
}

/**
 * The `meTab` keys a change of the open review source projects, for the host's
 * write watcher. Only the kind that opened or closed is written (a group open
 * never writes `artemisReviewOpen`), and `source` is null after a close, so the
 * kind that ENDED is read from `prev`. The ended kind's close is applied first
 * and the opened kind's open over it, so two kinds sharing a flag hand it over
 * open. An open also rests the id of the kind it excludes at null, so the URL
 * written in-session is the one the validator would read back after a reload.
 *
 * @param {Object|null} next the store's source now
 * @param {Object|null} prev the store's source before
 * @returns {Object} the keys to write; empty when no registered kind moved
 */
export function reviewDeepLinkPatch(next, prev) {
  const nextKind = next?.kind ?? null;
  const prevKind = prev?.kind ?? null;
  const patch = {};

  const ended = prevKind !== nextKind ? linkFor(prevKind) : null;
  if (ended) Object.assign(patch, { [ended.openKey]: false }, ended.closePatch);

  const opened = linkFor(nextKind);
  if (opened) Object.assign(patch, exclusionPatch(opened), { [opened.openKey]: true }, opened.openPatch(next));

  return patch;
}

/**
 * The review source a `meTab` section asks the host to open: the first kind the
 * host hydrates whose flag is set and whose id is valid and not excluded, or
 * null. The validator coerces a malformed id to null, so null covers both the
 * never-deep-linked case and the malformed one; the exclusion is read here too,
 * so a raw replay naming two kinds opens the one a decoded link would.
 *
 * @param {Object|null} meTab
 * @returns {Object|null} a `source` for `changesetReview.openReview`
 */
export function reviewSourceFromDeepLink(meTab) {
  if (!meTab || typeof meTab !== "object") return null;
  const excluded = excludedKinds(meTab);
  for (const [kind, link] of Object.entries(REVIEW_SOURCE_DEEP_LINKS)) {
    if (!link.toSource || excluded.has(kind)) continue;
    if (meTab[link.openKey] !== true) continue;
    if (!link.isId(meTab[link.idKey])) continue;
    return link.toSource(meTab);
  }
  return null;
}

/**
 * The Me sub-tab a `meTab` section's review lands on when its link names no
 * sub-tab, or null. A review is named by its flag OR its id, read under the
 * validator's own contracts (the exclusion included), so a raw replay value
 * reads the way a decoded one does. `migrateLegacyMeTabInboxShape` reads it.
 *
 * @param {Object} meTab
 * @returns {string|null}
 */
export function reviewDeepLinkLandingTab(meTab) {
  const excluded = excludedKinds(meTab);
  for (const [kind, link] of Object.entries(REVIEW_SOURCE_DEEP_LINKS)) {
    if (link.landingTab == null || excluded.has(kind)) continue;
    if (meTab[link.openKey] === true || link.isId(meTab[link.idKey])) return link.landingTab;
  }
  return null;
}
